/**
 * Pick the name to show in the "Your session with X has been booked..."
 * banner. The viewing user always sees the OTHER party's name -- the
 * expert sees the booker, the booker sees the expert.
 *
 * Used by both ritual-group session chats (RitualGroupChatArea) and the
 * legacy DM-based session chats (DmChatArea). Their booking context
 * DTOs are deliberately shaped the same in this regard so a single
 * helper drives both.
 */

export interface BookingCounterpartyArgs {
  currentUserId: string | undefined
  expertId: string | null
  expertName: string | null
  participantId: string | null
  participantName: string | null
}

/** Used when the viewer is the expert but participantName is null
 * (missing user join, deleted booker, or older cached payload). */
const FALLBACK_PARTICIPANT_NAME = "the participant"

/** Used when the viewer is the booker (or unidentified) and expertName
 * isn't populated. Matches the existing banner copy. */
const FALLBACK_EXPERT_NAME = "your expert"

export function bookingCounterpartyName(args: BookingCounterpartyArgs): string {
  // Identify the viewer symmetrically against both ids -- never assume
  // "the other one" by elimination. A stale DTO that swaps expert/
  // participant ids around shouldn't quietly show the viewer their own
  // name. Booker-side path is the default fallback because expertName
  // is the long-standing banner field consumers expect.
  if (args.currentUserId) {
    if (args.currentUserId === args.expertId) {
      return args.participantName ?? FALLBACK_PARTICIPANT_NAME
    }
    if (args.currentUserId === args.participantId) {
      return args.expertName ?? FALLBACK_EXPERT_NAME
    }
  }
  return args.expertName ?? FALLBACK_EXPERT_NAME
}
